import { FP, RemoteData } from "../deps"
import * as ServiceErrors from "../service-errors/service-errors"

/**
 * @see https://shopify.dev/api/ajax/reference/cart#get-cart-js
 */
export type ShopifyCartData = {
  token: string
  item_count: number
  total_price: number
  total_weight: number
  cart_level_discount_applications: [
    {
      type: string
      key: string
      title: string
      /** Decimal */
      value: string
      total_allocated_amount: number
    }
  ]
}

function assertIsShopifyCartData(
  input: unknown
): asserts input is ShopifyCartData {
  const data = input as ShopifyCartData
  if (typeof data !== "object") {
    throw new Error("Expected object")
  }
  if (!Array.isArray(data.cart_level_discount_applications)) {
    throw new Error("Expected cart_level_discount_applications to be an array")
  }
}

/**
 * GET /cart.js
 * @see https://shopify.dev/api/ajax/reference/cart#get-cart-js
 */
export const getCart: () => FP.task.Task<
  RemoteData.RemoteData<Error, ShopifyCartData>
> = FP.function.flow(
  FP.taskEither.tryCatchK(
    () => fetch("/cart.js"),
    (error) =>
      new Error(
        `Failed retrieving shopify cart data: ${
          error instanceof Error ? error.message : JSON.stringify(error)
        }`
      )
  ),
  FP.taskEither.chain(
    FP.taskEither.tryCatchK(
      (result) => result.json(),
      (error) =>
        new Error(
          `Failed parsing shopify cart data: ${
            error instanceof Error ? error.message : JSON.stringify(error)
          }`
        )
    )
  ),
  FP.taskEither.chainEitherK(
    FP.either.tryCatchK(
      (response: unknown) => {
        assertIsShopifyCartData(response)
        return response
      },
      (error) =>
        new Error(
          `Failed parsing shopify cart data: ${
            error instanceof Error ? error.message : JSON.stringify(error)
          }`
        )
    )
  ),

  FP.task.map(RemoteData.fromEither)
)

export const updateCart: (input: {
  attributes: Record<string, string>
}) => FP.taskEither.TaskEither<Error, void> = FP.function.flow(
  (input): RequestInit => ({
    headers: {
      "Content-Type": "application/json",
    },
    method: "POST",
    body: JSON.stringify(input),
  }),
  FP.taskEither.tryCatchK(
    (requestInit) => fetch("/cart/update.js", requestInit),
    (error) =>
      new Error(
        `Failed updating shopify cart data: ${
          error instanceof Error ? error.message : JSON.stringify(error)
        }`
      )
  ),
  FP.taskEither.map(FP.function.constVoid)
)

export const clearDiscountCode: () => FP.taskEither.TaskEither<Error, void> =
  FP.function.flow(
    FP.taskEither.tryCatchK(
      () => fetch(`/checkout?discount=%20&skip_shopify_pay=true`),
      (error) =>
        new Error(
          `Failed to clear discount code on checkout: ${
            error instanceof Error ? error.message : JSON.stringify(error)
          }`
        )
    ),
    FP.taskEither.map(FP.function.constVoid)
  )

export const applyDiscountCode: (
  discountCode: string
) => FP.taskEither.TaskEither<Error, void> = (discountCode) =>
  FP.function.pipe(
    // Clear all discount codes from checkout before applying new one, covers cases when customer removes/adds other
    clearDiscountCode(),
    ServiceErrors.recordTaskEitherError,
    FP.taskEither.fromTask,
    FP.taskEither.chain(
      FP.taskEither.tryCatchK(
        () => fetch(`/checkout?discount=${discountCode}&skip_shopify_pay=true`),
        (error) =>
          new Error(
            `Failed applying discount code: ${
              error instanceof Error ? error.message : JSON.stringify(error)
            }`
          )
      )
    ),
    FP.taskEither.filterOrElse(
      (response) => response.status === 200,
      (response) =>
        new Error(
          `Failed to apply discount code: ${response.status} ${response.statusText}`
        )
    ),
    FP.taskEither.chain(
      FP.taskEither.tryCatchK(
        (response) => response.text(),
        (error) =>
          new Error(
            `Failed to apply discount code: ${
              error instanceof Error ? error.message : JSON.stringify(error)
            }`
          )
      )
    ),
    FP.taskEither.filterOrElse(
      (responseBody) => responseBody.indexOf(discountCode) !== -1,
      (body) => {
        console.log(
          "Sellalong Debug: Discount code application response - " + body
        )
        return new Error(
          "Failed to apply discount code to checkout (not found on body)"
        )
      }
    ),
    FP.taskEither.map(FP.function.constVoid)
  )
